import { useCallback, useMemo, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled, { useTheme } from 'foundations-theming/utils';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { Button } from 'foundations-components/transitional/components/Button';
import { H1 } from 'foundations-components/transitional/components/H1';
import { getPrimaryButtonStyle, getPrimaryButtonHoverStyle } from 'MeetingsLib/utils/color';
import EmailFormFieldAsync from 'MeetingsLib/components/input/EmailFormFieldAsync';
// @ts-ignore ts-migrate(7016) FIXME: Could not find a declaration file for module '../.... Remove this comment to see the full error message
import EmailAddressPattern from 'PatternValidationJS/patterns/EmailAddress';
import * as FormActions from 'MeetingsLib/redux/actions/FormActions';
import { fetchPublicAvailabilityInfoForOwner as fetchBookInfoForOwner, fetchMeetingsUserForContactOwnerPrioritization as fetchMeetingsUser } from 'MeetingsLib/redux/actions/BookActions';
import { fetchTroubleshootTimes as fetchTroubleshootTimesAction } from 'MeetingsLib/redux/actions/TroubleshootActions';
import { isTroubleshootingSelector } from 'MeetingsLib/selectors/TroubleshootingSelectors';
import { getStagedPrefillEmail } from 'MeetingsLib/selectors/PrefillSelectors';
import { getIsValidatingStagedEmail, getStagedEmailValidationResults, getStagedEmailValidationError } from 'MeetingsLib/selectors/FormFieldValidationSelector';
import { useAppSelector } from 'MeetingsLib/redux/reducers';
import PreviewContext from 'MeetingsLib/utils/PreviewContext';
import CompanyLogo, { COMPANY_LOGO_USE } from 'MeetingsLib/components/decorative/CompanyLogo';
import { Text } from 'foundations-components/components/Text';
import { Image } from 'foundations-components/transitional/components/Image';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EmailFormContainer = styled.div.withConfig({
  displayName: "EmailFormV2__EmailFormContainer"
})(({
  theme,
  compact,
  primaryColor
}) => ({
  display: 'flex',
  justifyContent: 'space-around',
  padding: theme.space['500'],
  backgroundColor: theme.color['fill-surface-default'],
  borderWidth: compact ? '20px' : '40px',
  borderStyle: 'solid',
  borderColor: primaryColor || theme.color['fill-primary-default']
}));
// Styled wrapper that targets the Button component inside it
const ButtonWrapper = styled.div.withConfig({
  displayName: "EmailFormV2__ButtonWrapper"
})(["> button{", "}"], props => !props.disabled && `
      border-color: ${props.customStyle.borderColor} !important;
      background-color: ${props.customStyle.backgroundColor} !important;

      &:hover {
        background-color: ${props.customStyle.backgroundColor} !important;
        opacity: 0.85;
      }
    `);

// TODO: Function component can use useSelector hook instead of connect
const EmailForm = ({
  EmailFormMarker,
  branding,
  compact,
  description,
  fetchMeetingsUserForContactOwnerPrioritization,
  fetchPublicAvailabilityInfoForOwner,
  fetchTroubleshootTimes,
  isTroubleshooting,
  logoUrl,
  meetingChangeId,
  prefillEmail,
  setEmailForm,
  title,
  useCompanyLogo
}) => {
  const theme = useTheme();
  const [buttonHover, setButtonHover] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const isValidating = useAppSelector(getIsValidatingStagedEmail);
  const emailValidationResults = useAppSelector(getStagedEmailValidationResults);
  const emailValidationError = useAppSelector(getStagedEmailValidationError);
  const [email, setEmail] = useState(prefillEmail);
  const isEmailPattern = useMemo(() => {
    return Boolean(email && EmailAddressPattern.test(email));
  }, [email]);
  const shouldDisableSubmit = useMemo(() => {
    if (!initialLoadComplete) {
      // Prevent enable during initial load so that the button does not flicker
      return true;
    }
    if (emailValidationError && isEmailPattern) {
      // If there's an email validation network error, submission is allowed
      return false;
    }
    return !isEmailPattern || isValidating || Boolean(emailValidationResults && !emailValidationResults.isValidEmail);
  }, [emailValidationError, emailValidationResults, initialLoadComplete, isEmailPattern, isValidating]);
  useEffect(() => {
    if (!isValidating && (emailValidationResults || emailValidationError) && !initialLoadComplete) {
      setInitialLoadComplete(true);
    }
  }, [isValidating, emailValidationResults, initialLoadComplete, emailValidationError]);
  const onEmailChange = useCallback((_fieldName, newEmail) => {
    setEmail(newEmail);
  }, []);
  const onMouseEnter = useCallback(() => {
    setButtonHover(true);
  }, []);
  const onMouseLeave = useCallback(() => {
    setButtonHover(false);
  }, []);
  const getValidationMessage = useCallback(() => {
    if (isEmailPattern) {
      return null;
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "prebook.details.errorHelper.email"
    });
  }, [isEmailPattern]);
  const handleEmailSubmit = useCallback(e => {
    e.preventDefault();
    setEmailForm(email);
    if (meetingChangeId) {
      fetchPublicAvailabilityInfoForOwner({
        email,
        meetingChangeId
      });
    } else {
      fetchPublicAvailabilityInfoForOwner({
        email
      });
    }
    fetchMeetingsUserForContactOwnerPrioritization(email);
    // if troubleshooting fetch the troubleshoot times for the email
    if (isTroubleshooting) {
      fetchTroubleshootTimes();
    }
  }, [email, fetchPublicAvailabilityInfoForOwner, fetchMeetingsUserForContactOwnerPrioritization, fetchTroubleshootTimes, isTroubleshooting, meetingChangeId, setEmailForm]);
  const renderedButton = useMemo(() => {
    const style = buttonHover ? getPrimaryButtonHoverStyle(branding.primaryColor, theme) : getPrimaryButtonStyle(branding.primaryColor, theme);
    return /*#__PURE__*/_jsx(View, {
      textAlign: "center",
      marginBlockStart: "500",
      children: /*#__PURE__*/_jsx(PreviewContext.Consumer, {
        children: isPreview =>
        /*#__PURE__*/
        /* @bend-expect trellis-tokenized we need to use a wrapper div to apply custom branding styles */
        _jsx(ButtonWrapper, {
          customStyle: style,
          disabled: shouldDisableSubmit,
          children: /*#__PURE__*/_jsx(Button, {
            use: "primary",
            disabled: shouldDisableSubmit,
            "data-test-id": "email-form-submit",
            onClick: e => !isPreview && handleEmailSubmit(e),
            size: "medium",
            onMouseEnter: onMouseEnter,
            onMouseLeave: onMouseLeave,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "emailForm.button"
            })
          })
        })
      })
    });
  }, [branding.primaryColor, buttonHover, handleEmailSubmit, onMouseEnter, onMouseLeave, shouldDisableSubmit, theme]);
  const renderedAvatar = useMemo(() => {
    if (!useCompanyLogo && !logoUrl) {
      return /*#__PURE__*/_jsx(Illustration, {
        name: "meetings",
        width: "60%"
      });
    } else if (useCompanyLogo) {
      return /*#__PURE__*/_jsx(View, {
        textAlign: "center",
        marginBlockStart: "400",
        marginInline: "1400",
        children: /*#__PURE__*/_jsx(CompanyLogo, {
          branding: branding,
          use: COMPANY_LOGO_USE.EMAIL_FORM,
          widthOverride: "auto"
        })
      });
    } else {
      return /*#__PURE__*/_jsx(View, {
        textAlign: "center",
        marginBlockStart: "400",
        children: /*#__PURE__*/_jsx(Image, {
          src: logoUrl,
          shape: "circle",
          width: 200,
          height: 200,
          margin: "auto"
        })
      });
    }
  }, [branding, logoUrl, useCompanyLogo]);
  return (
    /*#__PURE__*/
    /* @bend-expect trellis-tokenized */
    _jsx(EmailFormContainer, {
      compact: compact,
      primaryColor: branding.primaryColor,
      id: "meetings-email-form-border",
      children: /*#__PURE__*/_jsxs(View, {
        inlineSize: "350px",
        children: [/*#__PURE__*/_jsxs(View, {
          textAlign: "center",
          marginBlockStart: "400",
          children: [renderedAvatar, /*#__PURE__*/_jsx(H1, {
            marginBlock: "200",
            children: title || /*#__PURE__*/_jsx(FormattedMessage, {
              message: "emailForm.header"
            })
          }), /*#__PURE__*/_jsx(Text, {
            marginBlockEnd: "400",
            as: "p",
            children: description || /*#__PURE__*/_jsx(FormattedMessage, {
              message: "emailForm.text"
            })
          })]
        }), /*#__PURE__*/_jsx(EmailFormFieldAsync, {
          fieldLabel: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "emailForm.placeholder"
          }),
          validation: getValidationMessage(),
          fieldValue: email,
          isRequired: true,
          setFormValue: onEmailChange,
          autocomplete: "on",
          disabled: false
        }), renderedButton, /*#__PURE__*/_jsx(EmailFormMarker, {})]
      })
    })
  );
};
export default connect(state => ({
  prefillEmail: getStagedPrefillEmail(state),
  isTroubleshooting: isTroubleshootingSelector(state)
}), Object.assign({}, FormActions, {
  fetchPublicAvailabilityInfoForOwner: fetchBookInfoForOwner,
  fetchMeetingsUserForContactOwnerPrioritization: fetchMeetingsUser,
  fetchTroubleshootTimes: fetchTroubleshootTimesAction
}) /*  @ts-ignore Jun-18-2025, 19:10UTC fix types  */)(EmailForm);